import axios from "axios";

const BASE_URLS = {
  MAIN: "https://api.jdecor.in/API/REPORT/",
  OTHER: "https://api.jdecor.in/BADRI/API/",
  AUTH: "https://api.jdecor.in/API/AUTH/",
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleLogout = () => {
  const token = localStorage.getItem("token");
  // Only redirect if we haven't already cleared the session
  if (token) {
    localStorage.clear();
    window.location.href = "/login";
  }
};

// Function to handle token refresh
const refreshToken = async () => {
  const token = localStorage.getItem("token");
  const loginname = localStorage.getItem("loginname");
  const deviceId = localStorage.getItem("device_id");

  if (!token || !loginname || !deviceId) return null;

  try {
    const res = await axios.post(BASE_URLS.AUTH, {
      grant_type: "refresh_token",
      device_id: deviceId
    }, {
      headers: {
        loginname: loginname,
        token: token,
        "Content-Type": "application/json"
      }
    });

    if (res.data.Status === 1 && res.data.token) {
      localStorage.setItem("token", res.data.token);
      return res.data.token;
    }
  } catch (error) {
    console.error("Refresh token failed", error);
  }
  return null;
};

const createClient = (baseURL) => {
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    const deviceId = localStorage.getItem("device_id");
    const loginname = localStorage.getItem("loginname");

    if (token) {
      config.headers.token = token;
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (deviceId) {
      config.headers["device_id"] = deviceId;
    }

    if (loginname) {
      config.headers["loginname"] = loginname;
    }

    return config;
  });

  instance.interceptors.response.use(
    async (response) => {
      const { data, config } = response;
      const originalRequest = config;

      // Unauthorised Access or Missing/Invalid Credentials check
      const isUnauthorized = data && data.Status === "0" && 
        (data.message === "Unauthorised Access" || data.message === "Missing or Invalid Credentials");

      const isApproved = localStorage.getItem("isApproved") !== "false";

      if (isUnauthorized && isApproved && !originalRequest._retry) {
        // If we don't even have a token anymore, just logout
        if (!localStorage.getItem("token")) {
          handleLogout();
          return response;
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.token = token;
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshToken();
          if (newToken) {
            processQueue(null, newToken);
            originalRequest.headers.token = newToken;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          } else {
            processQueue(new Error("Session Expired"));
            handleLogout();
          }
        } catch (refreshError) {
          processQueue(refreshError);
          handleLogout();
        } finally {
          isRefreshing = false;
        }
      }
      return response;
    },
    async (error) => {
      const { config, response } = error;
      const originalRequest = config;

      const isApproved = localStorage.getItem("isApproved") !== "false";

      if (isApproved && response && response.data && response.data.Status === "0" && 
         (response.data.message === "Unauthorised Access" || response.data.message === "Missing or Invalid Credentials")) {
        
        if (!localStorage.getItem("token")) {
          handleLogout();
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.token = token;
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshToken();
          if (newToken) {
            processQueue(null, newToken);
            originalRequest.headers.token = newToken;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          } else {
            processQueue(new Error("Session Expired"));
            handleLogout();
          }
        } catch (refreshError) {
          processQueue(refreshError);
          handleLogout();
        } finally {
          isRefreshing = false;
        }
      }

      console.error("API ERROR:", error?.response || error.message);
      return Promise.reject(error);
    }
  );

  return instance;
};

export const apiClient = createClient(BASE_URLS.MAIN);
export const apiClientOther = createClient(BASE_URLS.OTHER);
export const apiClientAuth = createClient(BASE_URLS.AUTH);